import type { OfferActionKind } from "@shared/types/roomOffer";
import type { OfferStatus } from "@shared/roomOffers";
import { DAYS_OF_WEEK, type DayOfWeek } from "@shared/types/foyer";
import { translateKey, type Translate } from "../../i18n/translate";
import type { Formatters } from "../../i18n/format";

/**
 * 422 codes as a field plus a sentence (`offers:offerCopy.problems.<code>`).
 * An unmapped code still renders a generic sentence carrying the raw code.
 */

/** Where in the form a problem belongs. Drives which control turns red. */
export type OfferField =
  | "title"
  | "action"
  | "price"
  | "fulfil"
  | "slots"
  | "availability"
  | "items"
  | "image";

export interface OfferProblem {
  code: string;
  field: OfferField;
  message: string;
}

/** Code → field. The sentence is `offers:offerCopy.problems.<code>`. */
const PROBLEM_FIELDS: Record<string, OfferField> = {
  title_required: "title",
  action_missing: "action",
  action_kind_invalid: "action",
  promotion_takes_no_price: "action",
  promotion_takes_no_items: "items",
  order_fulfil_invalid: "fulfil",
  price_amount_invalid: "price",
  price_currency_invalid: "price",
  slots_missing: "slots",
  slots_mode_invalid: "slots",
  slots_days_invalid: "slots",
  slots_start_time_invalid: "slots",
  slots_end_time_invalid: "slots",
  slots_window_not_forward: "slots",
  slots_interval_invalid: "slots",
  slots_capacity_invalid: "slots",
  slots_horizon_invalid: "slots",
  slots_notice_invalid: "slots",
  availability_start_date_invalid: "availability",
  availability_end_date_invalid: "availability",
  availability_dates_reversed: "availability",
  availability_days_invalid: "availability",
  availability_start_time_invalid: "availability",
  availability_end_time_invalid: "availability",
  items_invalid: "items",
  item_invalid: "items",
  item_label_required: "items",
  override_invalid: "action",
  override_image_invalid: "image",
  item_id_duplicated: "items",
};

/** One code, as a field and a sentence. Never returns null — see the docblock. */
export function offerProblemMessage(t: Translate, code: string): OfferProblem {
  const field = PROBLEM_FIELDS[code];
  if (field) {
    return {
      code,
      field,
      message: translateKey(t, `offers:offerCopy.problems.${code}`),
    };
  }
  return {
    code,
    field: "action",
    message: t("offers:offerCopy.unknownProblem", { code }),
  };
}

/** A 422 body or a thrown error as messages; null unless it is an `invalid_offer` refusal. */
export function offerProblemsOf(
  t: Translate,
  err: unknown
): OfferProblem[] | null {
  const e = err as { code?: string; body?: { details?: unknown } } | null;
  if (!e || e.code !== "invalid_offer") return null;
  const details = e.body?.details;
  if (!Array.isArray(details)) return [];
  return details
    .filter((d): d is string => typeof d === "string")
    .map((code) => offerProblemMessage(t, code));
}

/** What the operator calls each kind. Model names are never user-facing. */
export function offerKindLabel(t: Translate, kind: OfferActionKind): string {
  return t(`offers:offerCopy.kind.${kind}`);
}

/** One line under each kind in the picker, saying what the guest gets. */
export function offerKindHint(t: Translate, kind: OfferActionKind): string {
  return t(`offers:offerCopy.kindHint.${kind}`);
}

/** "Off" (a switch flipped) and "Ended" (a date passed) are different facts; never merge them. */
export function offerStatusLabel(t: Translate, status: OfferStatus): string {
  return t(`offers:offerCopy.status.${status}`);
}

const MONDAY_UTC = Date.UTC(2024, 0, 1);
const DAY_MS = 24 * 60 * 60 * 1000;

/** `Mon` / `mån`: the weekday as the day pickers abbreviate it. */
export function shortDayLabel(fmt: Formatters, day: DayOfWeek): string {
  return fmt.date(MONDAY_UTC + DAYS_OF_WEEK.indexOf(day) * DAY_MS, {
    weekday: "short",
    timeZone: "UTC",
  });
}
